import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';

const WEBSOCKET_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3000';

interface UseWebSocketOptions {
  namespace?: string;
  autoConnect?: boolean;
}

// Singleton socket instance per namespace
const sockets = new Map<string, Socket>();
const connectionCounts = new Map<string, number>();

const getOrCreateSocket = (namespace: string): Socket => {
  if (!sockets.has(namespace)) {
    const socket = io(`${WEBSOCKET_URL}${namespace}`, {
      transports: ['polling', 'websocket'],
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    sockets.set(namespace, socket);
    connectionCounts.set(namespace, 0);
  }
  return sockets.get(namespace)!;
};

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const { namespace = '/auction', autoConnect = true } = options;
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!autoConnect) return;

    const socket = getOrCreateSocket(namespace);
    socketRef.current = socket;

    const count = connectionCounts.get(namespace) || 0;
    connectionCounts.set(namespace, count + 1);

    const handleConnect = () => {
      setIsConnected(true);
      setError(null);
    };

    const handleDisconnect = () => {
      setIsConnected(false);
    };

    const handleError = (err: Error) => {
      setError(err);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleError);

    // Set initial state
    setIsConnected(socket.connected);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleError);

      const currentCount = connectionCounts.get(namespace) || 0;
      const newCount = currentCount - 1;
      connectionCounts.set(namespace, newCount);

      // Delay disconnect to handle React Strict Mode double-mounting
      if (newCount === 0) {
        setTimeout(() => {
          const finalCount = connectionCounts.get(namespace) || 0;
          if (finalCount === 0) {
            socket.disconnect();
            sockets.delete(namespace);
            connectionCounts.delete(namespace);
          }
        }, 100);
      }
    };
  }, [namespace, autoConnect]);

  const emit = useCallback((event: string, data?: unknown) => {
    if (!socketRef.current) {
      console.warn('Socket not connected');
      return;
    }
    socketRef.current.emit(event, data);
  }, []);

  const on = useCallback((event: string, callback: (data: unknown) => void) => {
    const socket = socketRef.current;
    if (!socket) {
      console.warn('Socket not connected');
      return () => {};
    }
    socket.on(event, callback);
    return () => socket.off(event, callback);
  }, []);

  return {
    socket: socketRef.current,
    isConnected,
    error,
    emit,
    on,
  };
};
